import React, { useEffect, useState } from 'react';
import { StyledFormElementWrapper } from 'theme/styles';
import FormLabel from 'components/atoms/FormLabel';
import { IFormElementOptionProp, IFormElementProps } from 'models/form';
import { Controller, useController } from 'react-hook-form';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { EDictionaryTypes } from 'models/dictionary';
import { getDictionaryNamesLike } from 'requests/dictionary';
import { useTranslations } from 'hooks/useTranslations';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { StyledAutocomplete, StyledFormControl, StyledInnerWrapper, StyledTextField } from './styles';

interface IFormAutocomplete extends IFormElementProps {
  options?: IFormElementOptionProp[];
  className?: string;
  loadOptions?: (arg?: any) => Promise<IFormElementOptionProp[]>;
  freeSolo?: boolean;
  disabled?: boolean;
  required?: boolean;
  optionsLimiter?: EDictionaryTypes;
  charsLimit?: number;
}

enum EFormAutocompleteInputState {
  INITIAL = 'initial',
  SELECTED = 'selected',
  TYPING = 'typing',
  CLEARED = 'cleared',
}

enum EFormAutocompleteAction {
  FIELD_SYNC = 'fieldSync',
  VALIDATION = 'validation',
  EXACT_MATCH = 'exactMatch',
}

const stateMachine = {
  transitions: {
    [EFormAutocompleteInputState.INITIAL]: {
      onUserInput: EFormAutocompleteInputState.TYPING,
      onFieldSet: EFormAutocompleteInputState.SELECTED,
      onClear: EFormAutocompleteInputState.CLEARED,
    },
    [EFormAutocompleteInputState.TYPING]: {
      onExactMatch: EFormAutocompleteInputState.SELECTED,
      onClear: EFormAutocompleteInputState.CLEARED,
      onUserInput: EFormAutocompleteInputState.TYPING,
    },
    [EFormAutocompleteInputState.SELECTED]: {
      onUserInput: EFormAutocompleteInputState.TYPING,
      onClear: EFormAutocompleteInputState.CLEARED,
      onFieldSet: EFormAutocompleteInputState.SELECTED,
    },
    [EFormAutocompleteInputState.CLEARED]: {
      onUserInput: EFormAutocompleteInputState.TYPING,
      onFieldSet: EFormAutocompleteInputState.SELECTED,
    },
  },

  allowedActions: {
    [EFormAutocompleteInputState.INITIAL]: [
      EFormAutocompleteAction.FIELD_SYNC,
      EFormAutocompleteAction.VALIDATION,
    ],
    [EFormAutocompleteInputState.TYPING]: [
      EFormAutocompleteAction.VALIDATION,
      EFormAutocompleteAction.EXACT_MATCH,
    ],
    [EFormAutocompleteInputState.SELECTED]: [EFormAutocompleteAction.FIELD_SYNC],
    [EFormAutocompleteInputState.CLEARED]: [
      EFormAutocompleteAction.FIELD_SYNC,
      EFormAutocompleteAction.VALIDATION,
    ],
  },
};

const AUTOCOMPLETE_CHARS_LIMIT = 2;
const REQUEST_TIME_INTERVAL = 500;
const ERROR_TYPE = 'custom';

export const FormAutocomplete = ({
  name,
  label,
  control,
  errors,
  setError,
  clearErrors,
  className,
  options,
  loadOptions,
  freeSolo,
  disabled,
  required,
  optionsLimiter,
  charsLimit,
}: IFormAutocomplete) => {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const { field: cField } = useController({ name, control });
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const [inputState, setInputState] = useState<EFormAutocompleteInputState>(
    EFormAutocompleteInputState.INITIAL,
  );
  const defaultValue = options?.find((option) => option.value === cField.value);
  const { t } = useTranslations();
  const charsLimitAutocomplite = charsLimit ? charsLimit : AUTOCOMPLETE_CHARS_LIMIT;

  const [asyncLoadedOptions, setAsyncLoadedOptions] = useState<IFormElementOptionProp[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  const getAllOptions = () => [...(options || []), ...asyncLoadedOptions];

  const transitionTo = (newState: EFormAutocompleteInputState) => {
    setInputState(newState);
  };

  const canPerformAction = (action: EFormAutocompleteAction) =>
    stateMachine.allowedActions[inputState]?.includes(action) || false;

  const handleEmptyInput = () => {
    cField.onChange('');
    clearErrors?.(name);
  };

  const findExactMatch = (value: string) =>
    getAllOptions().find((option) => option.label.toLowerCase() === value.toLowerCase());

  const handleExactMatch = (exactMatch: IFormElementOptionProp) => {
    cField.onChange(exactMatch.value);
  };

  const isInOptions = (value: string) => {
    const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`^${escapeRegex(value.toLowerCase())}`, 'i');
    return getAllOptions().some((option) => regex.test(option.label.toLowerCase()));
  };

  const validateInput = (value: string) => {
    if (isInOptions(value)) {
      if (open) clearErrors?.(name);
    } else {
      setError?.(name, {
        type: ERROR_TYPE,
        message: t('formAutocomplete.validation.text'),
      });
      cField.onChange('');
    }
  };

  const stateActions = {
    fieldSync: () => {
      if (!canPerformAction(EFormAutocompleteAction.FIELD_SYNC)) return;

      if (cField.value === '') {
        if (inputValue !== '') {
          setInputValue('');
          transitionTo(EFormAutocompleteInputState.CLEARED);
        }
      } else if (cField.value && inputState !== EFormAutocompleteInputState.SELECTED) {
        const displayValue = defaultValue?.label || cField.value;
        setInputValue(displayValue);
        transitionTo(EFormAutocompleteInputState.SELECTED);
      }
    },

    validation: (value: string) => {
      if (!canPerformAction(EFormAutocompleteAction.VALIDATION) || !setError || !clearErrors)
        return;

      const isEmpty = value.length === 0;
      const hasMinimumChars = value.length >= charsLimitAutocomplite;
      const hasAvailableOptions = options?.length || asyncLoadedOptions.length;

      if (isEmpty) {
        handleEmptyInput();
        return;
      }

      if (!hasAvailableOptions || !hasMinimumChars) {
        return;
      }

      if (canPerformAction(EFormAutocompleteAction.EXACT_MATCH)) {
        const matchingOption = findExactMatch(value);
        if (matchingOption && cField.value !== matchingOption.value) {
          handleExactMatch(matchingOption);
          transitionTo(EFormAutocompleteInputState.SELECTED);
          return;
        }
      }

      validateInput(value);
    },

    userInput: (newValue: string) => {
      setInputValue(newValue);
      transitionTo(EFormAutocompleteInputState.TYPING);
    },

    exactMatch: (option: IFormElementOptionProp) => {
      if (!canPerformAction(EFormAutocompleteAction.EXACT_MATCH)) return;
      handleExactMatch(option);
      transitionTo(EFormAutocompleteInputState.SELECTED);
    },

    clear: () => {
      handleEmptyInput();
      transitionTo(EFormAutocompleteInputState.CLEARED);
    },
  };

  useEffect(() => {
    if (inputState !== EFormAutocompleteInputState.TYPING) {
      stateActions.fieldSync();
    }
  }, [cField.value, defaultValue, optionsLimiter, inputState]);

  const changeAsyncLoadedOptionsState = async (isFetchMore?: boolean) => {
    if (loadOptions) {
      setLoading(true);
      const values = await loadOptions(inputValue?.trim());
      setAsyncLoadedOptions((prevState) =>
        [...(isFetchMore ? prevState : []), ...values].map(
          (option: IFormElementOptionProp) => ({
            label: option.label,
            value: option.value,
          }),
        ),
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    if (inputState !== EFormAutocompleteInputState.TYPING) {
      return;
    }

    if (loadOptions && inputValue?.trim()?.length >= charsLimitAutocomplite) {
      (async () => {
        await changeAsyncLoadedOptionsState();
      })();
    }
  }, [inputValue, inputState]);

  useEffect(() => {
    if (optionsLimiter && inputValue && inputValue?.trim()?.length >= charsLimitAutocomplite) {
      const currentTime = new Date().getTime();
      const timeSinceLastRequest = currentTime - lastRequestTime;

      if (timeSinceLastRequest >= REQUEST_TIME_INTERVAL) {
        setLastRequestTime(currentTime);
        (async function init() {
          const response = await getDictionaryNamesLike(optionsLimiter, {
            searchValue: inputValue,
          });
          if (response) {
            const values = Object.values(response) as unknown as string[];
            const formattedOptions = values.map((value: string) => ({
              label: value,
              value,
            }));
            setAsyncLoadedOptions(formattedOptions);
          }
        })();
      }
    }

    if (
      optionsLimiter &&
      inputValue?.trim()?.length > 0 &&
      inputValue?.trim()?.length < charsLimitAutocomplite &&
      !cField.value
    ) {
      setAsyncLoadedOptions([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const handleOpen = () => {
    if (inputValue.length >= charsLimitAutocomplite) {
      setOpen(true);
    }
  };

  useEffect(() => {
    stateActions.validation(inputValue);
  }, [options, asyncLoadedOptions, inputValue]);

  return (
    <StyledFormElementWrapper className={className}>
      <FormLabel name={name} label={label} disabled={disabled} required={required} />
      <StyledInnerWrapper>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <StyledFormControl className={className} error={!!errors[name]?.message}>
              <StyledAutocomplete
                open={open}
                onOpen={handleOpen}
                onClose={() => setOpen(false)}
                inputValue={inputValue}
                forcePopupIcon={false}
                popupIcon={null}
                clearIcon={<Icon type={EIconTypes.clear} />}
                onInputChange={(
                  event: React.SyntheticEvent,
                  newInputValue: string,
                  reason: string,
                ) => {
                  if (event) {
                    setInputValue(newInputValue);
                    if (reason === 'input') {
                      transitionTo(EFormAutocompleteInputState.TYPING);
                      if (newInputValue.length >= charsLimitAutocomplite) {
                        setOpen(true);
                      } else {
                        setOpen(false);
                      }
                    }
                  }
                }}
                getOptionLabel={(option: any) => option.label}
                isOptionEqualToValue={(option: any, value: any) =>
                  option.value === value.value
                }
                onChange={(e, data: any) => {
                  field.onChange(data?.value || '');
                  transitionTo(EFormAutocompleteInputState.SELECTED);
                }}
                loading={loading}
                freeSolo={freeSolo}
                options={options || asyncLoadedOptions}
                renderInput={(params) => (
                  <StyledTextField
                    ref={field.ref}
                    {...params}
                    placeholder={t('formAutoComplete.all.placeholder')}
                  />
                )}
                disabled={disabled}
                id={name}
              />
            </StyledFormControl>
          )}
        />
      </StyledInnerWrapper>
      <FormErrorLabel label={errors[name]?.message} />
    </StyledFormElementWrapper>
  );
};
